import { Box, Checkbox, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalDialog, DotLegalEmptyState, DotLegalMultiSelect, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalTableWithControlsLocalized from "@common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import EmptyStateIcon from "@common/svgs/emptyState.svg?react";
import { SelectableEntityRow, useAddEntitiesDialog } from "./AddEntitiesDialog.hooks";

export interface IAddEntitiesDialog {
    riskAreaId: string;
    onSaved: () => void;
    onClose: () => void;
}

function AddEntitiesDialog(props: IAddEntitiesDialog) {
    const { translateString } = useTranslation();
    const {
        isLoading,
        rows,
        hasEntities,
        typeOptions,
        selectedTypes,
        setSelectedTypes,
        toggleEntity,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        setSearchString,
        onSaveClick,
        isSaving,
        hasSelection,
        selectedCount,
    } = useAddEntitiesDialog(props);

    const headers: Array<ITableHeader<SelectableEntityRow>> = [
        { property: "name", text: translateString("entity"), align: "left", showOnMobile: true },
        { property: "typeLabel", text: translateString("type"), align: "left", showOnMobile: true },
    ];

    return (
        <DotLegalDialog
            open
            header={translateString("addEntities")}
            buttonOkText={selectedCount > 0 ? `${translateString("add")} (${selectedCount})` : translateString("add")}
            secondaryButtonText={translateString("cancel")}
            onOkClick={onSaveClick}
            onSecondaryButtonClick={props.onClose}
            onDialogClose={props.onClose}
            okButtonDisabled={!hasSelection}
            inProgress={isSaving}
            size="lg"
        >
            {!isLoading && !hasEntities ? (
                <DotLegalEmptyState icon={<EmptyStateIcon />} text={translateString("noEntitiesToAdd")} />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", height: 700 }}>
                    <DotLegalTableWithControlsLocalized
                        headers={headers}
                        data={rows}
                        emptyText={translateString("noDataMatchingCriteria")}
                        isLoading={isLoading}
                        defaultOrderBy="name"
                        defaultOrder="asc"
                        columnsExcludedFromFilter={["key", "type", "isSelected"]}
                        getUserSpecificPageLength={() => 30}
                        clickableRows={false}
                        onControlChange={(search) => setSearchString(search)}
                        onSelectAllChanged={onSelectAllChanged}
                        headerCheckBoxState={getHeaderCheckBoxState()}
                        extraControls={
                            <Grid size={{ xs: 4 }}>
                                <DotLegalMultiSelect
                                    limit={1}
                                    onChange={setSelectedTypes}
                                    chosenOptions={selectedTypes}
                                    label={translateString("type")}
                                    placeholder={translateString("type")}
                                    options={typeOptions}
                                    noMargin
                                    noOptionsLabel={translateString("noOptions")}
                                />
                            </Grid>
                        }
                        renderRow={(row) => (
                            <TableRow key={row.key} hover sx={{ cursor: "pointer" }} onClick={() => toggleEntity(row.key)}>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={row.isSelected} readOnly tabIndex={-1} />
                                </TableCell>
                                <TableCell>
                                    <EntityTypeIconWithName type={row.type} name={row.name} />
                                </TableCell>
                                <TableCell>{row.typeLabel}</TableCell>
                            </TableRow>
                        )}
                    />
                </Box>
            )}
        </DotLegalDialog>
    );
}

export default AddEntitiesDialog;
